import type { World } from '../World';
import type { RunState } from '../run/RunState';
import {
  skillParams, type AbilityId, type AbilityInstance,
} from './AbilityDefs';
import { SHOT_KINDS, spawnPlayerShot } from './shots';
import { dist2 } from '../../core/math';
import { aspectDamageMult, aspectOnFloorEnter } from '../characters/aspects';

/**
 * Hook dispatch for passive skills that live at World funnels rather than in
 * the AbilitySystem cooldown switch: on-kill triggers (Rupture, Splinter,
 * Caustic, Brand), contact retaliation (Recoil), and live outgoing-damage
 * scaling (Tithe, aspects). Knell/Threshold moved to the artifact pool in
 * M0.5 (Passing Bell / Wayfarer's Ward).
 *
 * All consumers are live-position/gameplayRng-free — never seeded streams.
 * Corpses are drained ITERATIVELY from World.pendingCorpses so chain
 * reactions (a Rupture kill spawning more corpses) resolve in-tick without
 * recursion.
 */

/** A death this tick, queued at the kill site (World.killEnemy). */
export interface Corpse {
  x: number;
  y: number;
  type: string;
  isElite: boolean;
  wasChilled: boolean;
  wasBurning: boolean;
  wasBranded: boolean;
  /** Bullet kind of the direct hit that killed (−1 = not a player shot). */
  killedByKind: number;
  /** True when this death came from an on-kill chain (Rupture) — the chain
   *  fades (×RUPTURE_CHAIN_FALLOFF) unless Sympathetic Charge. */
  viaDetonation: boolean;
}

// S2 debuff constants (applied at the World.damageEnemy funnel). All (tuning).
export const BRAND_AMP = 1.25;
export const SHRED_PER_STACK = 0.05;
export const SHRED_MAX_STACKS = 8;
export const RUPTURE_CHAIN_FALLOFF = 0.6;
/** Brand's boss-duel fallback (M0.5): after this long with no kill feeding
 *  the marks, the next HIT brands its target directly — kill-fed in crowds,
 *  a keep-the-mark rhythm in add-free duels (the M0 "boss shouldn't turn
 *  your kit off" rule applied to a pool skill). */
export const BRAND_QUIET_SECONDS = 6;
/** Tithe: unbanked gems → outgoing damage (rate per gem, hard cap). */
export const TITHE_RATE = 0.0006;
export const TITHE_CAP = 0.5;
/** Settlement arrival shock: damage per point of held Tithe bonus. */
export const SETTLEMENT_SHOCK_PER_BONUS = 60;
/** Blight Bloom growth per feeding death and its size ceiling. */
export const CAUSTIC_BLOOM_GROWTH = 1.1;
export const CAUSTIC_BLOOM_CAP = 1.6;
const CAUSTIC_POOL_CAP = 12;

function owned(rs: RunState, id: AbilityId): AbilityInstance | null {
  return rs.abilities.find((a) => a.defId === id) ?? null;
}

function hookDamageMult(rs: RunState): number {
  return rs.stats.resolve('damageMult') * skillOutgoingDamageMult(rs);
}

/** On-kill skill triggers, one corpse at a time (drained by World.update). */
export function skillOnKill(world: World, corpse: Corpse): void {
  const rs = world.runState;
  const dmgMult = hookDamageMult(rs);
  const areaMult = rs.stats.resolve('areaMult');

  const rupture = owned(rs, 'rupture');
  if (rupture) {
    const p = skillParams(rupture);
    const radius = p.radius * areaMult;
    const falloff = corpse.viaDetonation && !rupture.upgraded ? RUPTURE_CHAIN_FALLOFF : 1;
    world.hash.query(corpse.x, corpse.y, radius + 30, (idx) => {
      const e = world.enemies[idx];
      if (!e || !e.alive) return;
      if (dist2(corpse.x, corpse.y, e.x, e.y) <= (radius + e.radius) ** 2) {
        world.damageEnemy(idx, p.damage * dmgMult * falloff);
      }
    });
    world.events.emit('corpseDetonated', { x: corpse.x, y: corpse.y, radius });
  }

  const splinter = owned(rs, 'splinter');
  if (splinter) {
    const p = skillParams(splinter);
    let target: { x: number; y: number } | null = null;
    let bestD = 500 * 500;
    for (const e of world.enemies) {
      if (!e.alive) continue;
      const d = dist2(corpse.x, corpse.y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        target = e;
      }
    }
    if (target) {
      const shards =
        splinter.upgraded && corpse.killedByKind === SHOT_KINDS.splinterShard ? 2 : 1;
      const aim = Math.atan2(target.y - corpse.y, target.x - corpse.x);
      for (let s = 0; s < shards; s++) {
        spawnPlayerShot(world, corpse.x, corpse.y, aim + (s === 0 ? 0 : 0.35),
          480, SHOT_KINDS.splinterShard, p.damage * dmgMult, { lifeSteps: 90 });
      }
    }
  }

  const caustic = owned(rs, 'caustic');
  if (caustic) {
    const p = skillParams(caustic);
    const baseR = p.radius * areaMult;
    // Merge rule (M0.5): a death inside an existing pool FEEDS it — life
    // refreshed, dps takes the max — instead of stacking an overlapping
    // pool. Overlap-stacked pools were most of the perceived OP and all of
    // the visual noise (burn zones already anti-stack via max at tickZones).
    // Blight Bloom's extra: the fed pool also GROWS.
    let fed = false;
    for (const zone of world.zones) {
      if (zone.kind !== 'caustic') continue;
      if (dist2(zone.x, zone.y, corpse.x, corpse.y) <= zone.radius * zone.radius) {
        zone.duration = p.duration;
        zone.dps = Math.max(zone.dps, p.damage * dmgMult);
        if (caustic.upgraded) {
          zone.radius = Math.min(baseR * CAUSTIC_BLOOM_CAP, zone.radius * CAUSTIC_BLOOM_GROWTH);
        }
        world.events.emit('causticFed', { x: zone.x, y: zone.y, radius: zone.radius });
        fed = true;
        break;
      }
    }
    if (!fed) {
      world.zones.push({
        x: corpse.x, y: corpse.y, radius: baseR, duration: p.duration,
        dps: p.damage * dmgMult, slowPct: 0, kind: 'caustic',
      });
      let pools = 0;
      for (let z = world.zones.length - 1; z >= 0; z--) {
        if (world.zones[z]!.kind !== 'caustic') continue;
        pools++;
        if (pools > CAUSTIC_POOL_CAP) world.zones.splice(z, 1);
      }
    }
  }

  const brand = owned(rs, 'brand');
  if (brand) {
    world.brandQuiet = 0; // marks are flowing — the quiet-hit fallback rests
    const p = skillParams(brand);
    const radius = p.radius * areaMult;
    world.hash.query(corpse.x, corpse.y, radius + 30, (idx) => {
      const e = world.enemies[idx];
      if (!e || !e.alive) return;
      if (dist2(corpse.x, corpse.y, e.x, e.y) <= (radius + e.radius) ** 2) {
        e.brandLeft = Math.max(e.brandLeft, p.duration);
      }
    });
    // Hexbrand: a branded death bursts every currently branded enemy.
    if (brand.upgraded && corpse.wasBranded) {
      for (let i = 0; i < world.enemies.length; i++) {
        const e = world.enemies[i]!;
        if (e.alive && e.brandLeft > 0) world.damageEnemy(i, p.damage * dmgMult);
      }
      world.events.emit('burstFired', { x: corpse.x, y: corpse.y, radius });
    }
  }
}

/**
 * Floor-arrival skill triggers (never fires for vault enter/exit). Arrival
 * worlds start empty (2s spawn grace) — the shocks mostly matter when the
 * settle happens late (re-entry style effects) and the i-frames/Sanctum
 * cover the first waves. Direct enemy loop: the spatial hash isn't built yet.
 */
export function skillOnFloorEnter(world: World): void {
  const rs = world.runState;
  const pl = world.player;
  const areaMult = rs.stats.resolve('areaMult');

  // Aspect arrivals (Mourne re-materializes his reserve — arrives armed).
  aspectOnFloorEnter(world);

  // Settlement (Tithe upgrade): the bonus held at descent lands as force.
  if (rs.titheSettlement > 0) {
    const radius = 200 * areaMult;
    shockAround(world, radius, SETTLEMENT_SHOCK_PER_BONUS * rs.titheSettlement);
    world.events.emit('thresholdShock', { x: pl.x, y: pl.y, radius });
    rs.titheSettlement = 0;
  }
}

function shockAround(world: World, radius: number, damage: number): void {
  const pl = world.player;
  for (let i = 0; i < world.enemies.length; i++) {
    const e = world.enemies[i]!;
    if (!e.alive) continue;
    if (dist2(pl.x, pl.y, e.x, e.y) <= (radius + e.radius) ** 2) {
      world.damageEnemy(i, damage);
    }
  }
}

/** The player took contact/bullet damage (post-mitigation): Recoil nova. */
export function skillOnPlayerHit(world: World): void {
  const rs = world.runState;
  const recoil = owned(rs, 'recoil');
  if (!recoil) return;
  const p = skillParams(recoil);
  const radius = p.radius * rs.stats.resolve('areaMult');
  const dmg = p.damage * hookDamageMult(rs);
  const pl = world.player;
  world.hash.query(pl.x, pl.y, radius + 30, (idx) => {
    const e = world.enemies[idx];
    if (!e || !e.alive) return;
    if (dist2(pl.x, pl.y, e.x, e.y) <= (radius + e.radius) ** 2) {
      world.damageEnemy(idx, dmg);
    }
  });
  if (recoil.upgraded) {
    const pool = world.enemyBullets;
    for (let i = 0; i < pool.count; i++) {
      if (dist2(pool.x[i]!, pool.y[i]!, pl.x, pl.y) <= radius * radius) {
        pool.despawn(i);
        i--;
      }
    }
  }
  world.events.emit('novaFired', { x: pl.x, y: pl.y, radius });
}

/**
 * Fracture stacking at the damageEnemy funnel — only for discrete 'hit'
 * damage (dots would stack 60×/s). Shatterpoint consumes full stacks.
 * Returns nothing; mutates the enemy and may deal burst damage.
 */
export function skillOnEnemyHit(world: World, enemyIdx: number): void {
  const rs = world.runState;
  const e = world.enemies[enemyIdx];
  if (!e || !e.alive) return;
  // Brand quiet-hit fallback (see BRAND_QUIET_SECONDS). Runs post-damage,
  // so the branding hit itself is never amplified — the NEXT one is.
  const brand = owned(rs, 'brand');
  if (brand && world.brandQuiet > BRAND_QUIET_SECONDS) {
    world.brandQuiet = 0;
    e.brandLeft = Math.max(e.brandLeft, skillParams(brand).duration);
  }
  const fracture = owned(rs, 'fracture');
  if (!fracture) return;
  if (fracture.upgraded && e.shredStacks >= SHRED_MAX_STACKS) {
    const p = skillParams(fracture);
    e.shredStacks = 0;
    e.stunLeft = Math.max(e.stunLeft, p.duration);
    world.events.emit('shatter', { x: e.x, y: e.y });
    world.damageEnemy(enemyIdx, p.damage * hookDamageMult(rs));
  } else if (e.shredStacks < SHRED_MAX_STACKS) {
    e.shredStacks++;
  }
}

/**
 * Live multiplier folded into every skill's outgoing damage (AbilitySystem
 * and the hooks above read it). Computed, never StatSheet churn.
 */
export function skillOutgoingDamageMult(rs: RunState): number {
  let mult = 1;
  // Tithe: the unbanked stake burns as damage until banked (or lost).
  if (owned(rs, 'tithe')) {
    mult *= 1 + Math.min(TITHE_CAP, rs.unbankedGems * TITHE_RATE);
  }
  // Aspects (Wick's Guttering: hottest right before he banks).
  mult *= aspectDamageMult(rs);
  return mult;
}
